import React, { useState, useRef, useEffect } from 'react';
import { useField } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiX, FiCheck } from 'react-icons/fi';
import { cn } from '../../lib/utils';

const FormSelect = ({
  name,
  label,
  options = [],
  placeholder = 'Select an option',
  required = false,
  helperText,
  searchable = true,
  clearable = true,
  loading = false,
  disabled = false,
  leftIcon,
  onSearch,
  className,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);
  const isSelectingRef = useRef(false);
  const hasError = meta.touched && meta.error;
  const id = props.id || name;

  const selectedOption = options.find((opt) => opt.value === field.value);
  const displayValue = selectedOption?.label || '';

  const isTyping = inputValue && inputValue !== displayValue;
  const filteredOptions = searchable && isTyping
    ? options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : options;

  useEffect(() => {
    if (!isOpen && selectedOption) {
      setInputValue(selectedOption.label);
    } else if (!isOpen && !field.value) {
      setInputValue('');
    }
  }, [field.value, isOpen, selectedOption]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setInputValue(displayValue);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, displayValue]);

  const handleSelect = (value) => {
    isSelectingRef.current = true;
    const option = options.find((opt) => opt.value === value);
    helpers.setValue(value);
    helpers.setTouched(true);
    setInputValue(option?.label || '');
    setIsOpen(false);
    setHighlightedIndex(-1);

    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    helpers.setValue('');
    helpers.setTouched(true);
    setInputValue('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (field.value && value !== displayValue) {
      helpers.setValue('');
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (inputRef.current && field.value) {
      inputRef.current.select();
    }
  };

  const handleInputBlur = () => {
    if (isSelectingRef.current) return;

    setTimeout(() => {
      if (!isSelectingRef.current) {
        if (!field.value) {
          setInputValue('');
        } else {
          setInputValue(displayValue);
        }
      }
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex((prev) => {
          const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0;
          setTimeout(() => {
            optionRefs.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1;
          setTimeout(() => {
            optionRefs.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setInputValue(displayValue);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className={cn('w-full', className)} ref={containerRef}>
      {/* External Label */}
      {label && (
        <label
          htmlFor={id}
          className={cn(
            'block text-sm font-medium mb-1.5',
            hasError ? 'text-danger-600' : 'text-neutral-700'
          )}
        >
          {label}
          {required && <span className="text-danger-500 ml-0.5">*</span>}
        </label>
      )}

      <div className="relative">
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400 z-10" aria-hidden="true">
              {React.cloneElement(leftIcon, { className: 'h-5 w-5' })}
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            id={id}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled || loading}
            placeholder={placeholder}
            autoComplete="off"
            className={cn(
              'w-full rounded-lg border py-2.5 text-sm transition-all duration-200',
              'placeholder:text-neutral-400',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              leftIcon ? 'pl-10' : 'pl-4',
              'pr-16',
              hasError
                ? 'border-danger-500 focus:ring-danger-500/20 bg-white'
                : isOpen
                ? 'border-primary-400 focus:border-primary-400 focus:ring-primary-300/30 bg-white'
                : 'border-neutral-300 hover:border-neutral-400 bg-white',
              disabled && 'bg-neutral-50 cursor-not-allowed opacity-60',
              'text-neutral-900'
            )}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-invalid={hasError}
            aria-autocomplete="list"
            role="combobox"
          />

          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {clearable && field.value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                tabIndex={-1}
                className="p-0.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
                aria-label="Clear selection"
              >
                <FiX className="h-4 w-4" />
              </button>
            )}

            <motion.div
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <FiChevronDown
                className={cn(
                  'h-4 w-4 transition-colors pointer-events-none',
                  hasError
                    ? 'text-danger-500'
                    : isOpen
                    ? 'text-primary-500'
                    : 'text-neutral-400'
                )}
              />
            </motion.div>
          </div>
        </div>

        {/* Dropdown Menu */}
        <AnimatePresence>
          {isOpen && filteredOptions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-neutral-200 overflow-hidden"
            >
              <ul role="listbox" className="max-h-60 overflow-y-auto py-1">
                {filteredOptions.map((option, index) => {
                  const isSelected = field.value === option.value;
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <li key={option.value} ref={(el) => (optionRefs.current[index] = el)}>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelect(option.value);
                        }}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={cn(
                          'w-full px-3 py-2 text-left text-sm transition-colors',
                          'flex items-center justify-between gap-2',
                          isSelected
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : isHighlighted
                            ? 'bg-neutral-100 text-neutral-900'
                            : 'text-neutral-700 hover:bg-neutral-50'
                        )}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <span className="flex-1">{option.label}</span>
                        {isSelected && <FiCheck className="h-4 w-4 text-primary-600 flex-shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}

          {isOpen && filteredOptions.length === 0 && inputValue && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-neutral-200 overflow-hidden"
            >
              <div className="px-3 py-4 text-center text-sm text-neutral-500">
                No results found
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error / Helper */}
      <div className="mt-1">
        {helperText && !hasError && (
          <p className="text-xs text-neutral-500">{helperText}</p>
        )}
        <AnimatePresence>
          {hasError && (
            <motion.p
              initial={{ opacity: 0, y: -5, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              id={`${id}-error`}
              className="text-xs text-danger-600 font-medium"
              role="alert"
            >
              {meta.error}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FormSelect;
